/* global trapFocus, removeTrapFocus */

// ILVA info icon (ILVA_Icon_Web_Info) exported from the Figma design system.
const INFO_ICON_SVG = `<svg viewBox="0 0 20 20" fill="none" aria-hidden="true">
  <path d="M3.9375 16.0625H16.0583V3.9375H3.9375V16.0583V16.0625ZM4.72917 4.72917H15.2667V15.2667H4.72917V4.72917Z" fill="currentColor"/>
  <path d="M10.6125 8.79584H8.075V9.5875H9.82083V12.7H7.49583V13.4917H9.82083H10.6125H12.7708V12.7H10.6125V8.79584Z" fill="currentColor"/>
  <path d="M10.0792 7.93333C10.5 7.93333 10.8417 7.59166 10.8417 7.17082C10.8417 6.74999 10.5 6.40833 10.0792 6.40833C9.65833 6.40833 9.31667 6.74999 9.31667 7.17082C9.31667 7.59166 9.65833 7.93333 10.0792 7.93333Z" fill="currentColor"/>
</svg>`;

class SwatchOrderForm extends HTMLElement {
  connectedCallback() {
    this.swatchData = JSON.parse(this.querySelector('[data-swatches-json]')?.textContent || '[]');
    this.maxSwatches = parseInt(this.dataset.maxSwatches || '5', 10);
    this.selected = new Set();

    this._restoreFromSession();
    this._renderGrid();
    this._renderSelectedSummary();
    this._initGridDelegate();
    this._initSummaryRemove();
    this._initFilters();
    this._initNavigation();
    this._initOverlays();
    this._initDetailPopup();
    this._initTermsPopup();
    this._initFormSubmit();
    this._initConsentToggle();
  }

  // ─── Overlays ─────────────────────────────────────────────────────────────
  // The swatch detail sheet and the club terms sheet both lock body scroll, so
  // they share one stack: the lock is only released when the last one closes,
  // and Escape always closes whichever is on top.

  _initOverlays() {
    this._overlays = [];

    document.addEventListener('keydown', (e) => {
      if (e.key !== 'Escape') return;
      const top = this._overlays[this._overlays.length - 1];
      if (top) top.close();
    });
  }

  _openOverlay(overlay, close) {
    if (this._overlays.some((entry) => entry.overlay === overlay)) return;
    const trigger = document.activeElement;
    const triggerHandle = trigger instanceof HTMLElement ? trigger.dataset.infoHandle : null;
    this._overlays.push({ overlay, close, trigger, triggerHandle });
    overlay.hidden = false;
    document.body.classList.add('overflow-hidden');
    this._trapOverlay(overlay);
  }

  _closeOverlay(overlay) {
    const index = this._overlays.findIndex((entry) => entry.overlay === overlay);
    if (index === -1) return;
    const [entry] = this._overlays.splice(index, 1);
    overlay.hidden = true;
    if (!this._overlays.length) document.body.classList.remove('overflow-hidden');

    const below = this._overlays[this._overlays.length - 1];
    let trigger = entry.trigger instanceof HTMLElement && entry.trigger.isConnected ? entry.trigger : null;

    if (!trigger && entry.triggerHandle) {
      trigger = this.querySelector(`[data-info-handle="${entry.triggerHandle}"]`);
    }

    if (below) {
      // A sheet is still open underneath — hand the trap back to it rather than
      // releasing focus to the page behind both.
      this._trapOverlay(below.overlay);
    } else if (typeof removeTrapFocus === 'function') {
      // Also returns focus to whatever opened the sheet — a swatch card or the terms button.
      removeTrapFocus(trigger);
    } else if (trigger) {
      trigger.focus();
    }
  }

  // trapFocus/removeTrapFocus are theme globals from main.js. That bundle is deferred,
  // so guard on them and fall back to plain focus if this element upgrades first.
  _trapOverlay(overlay) {
    const closeBtn = overlay.querySelector('.swatch-order__detail-close');
    if (typeof trapFocus === 'function') {
      trapFocus(overlay, closeBtn || overlay);
    } else {
      closeBtn?.focus();
    }
  }

  // ─── Session ──────────────────────────────────────────────────────────────

  _restoreFromSession() {
    try {
      const saved = sessionStorage.getItem('swatch-order-handles');
      if (!saved) return;
      // Drop handles that are no longer in the rendered data (excluded via the
      // section setting or deleted), so they can't count towards the max.
      const known = new Set(this.swatchData.map((sw) => sw.handle));
      JSON.parse(saved).forEach((h) => {
        if (known.has(h)) this.selected.add(h);
      });
    } catch (_) {}
  }

  _saveToSession() {
    try {
      sessionStorage.setItem('swatch-order-handles', JSON.stringify([...this.selected]));
    } catch (_) {}
  }

  _clearSession() {
    try {
      sessionStorage.removeItem('swatch-order-handles');
    } catch (_) {}
  }

  // ─── Filters ──────────────────────────────────────────────────────────────

  // base_color/material come from metaobject fields that may be a single
  // string or a list, so always normalize to an array of trimmed strings.
  _values(val) {
    return (Array.isArray(val) ? val : [val])
      .filter(Boolean)
      .map((v) => String(v).trim())
      .filter(Boolean);
  }

  // Both filters are the shared <custom-select> component, whose options are
  // server-rendered by the section. Nothing is built here on purpose: the
  // component captures its option list in its constructor (custom-select.js:7),
  // so an <option> appended afterwards would never reach the open listbox.
  //
  // The element read is the component's hidden native <select>, which
  // custom-select.js keeps in sync and fires a bubbling `change` on for every
  // pick (custom-select.js:416-418) — so the value and the event both still
  // come off a plain <select>, exactly as before.
  _filterSelect(group) {
    return this.querySelector(`[data-filter-group="${group}"] .custom-select__native`);
  }

  _initFilters() {
    this.querySelectorAll('[data-filter-group] .custom-select__native').forEach((el) => {
      el.addEventListener('change', () => this._renderGrid());
    });
  }

  // ─── Grid ─────────────────────────────────────────────────────────────────

  _renderGrid() {
    const grid = this.querySelector('[data-swatch-grid]');
    if (!grid) return;

    const fc = this._filterSelect('color')?.value || '';
    const fm = this._filterSelect('material')?.value || '';
    const isMaxed = this.selected.size >= this.maxSwatches;
    const order = [...this.selected];

    const filtered = this.swatchData.filter((sw) => {
      if (fc && !this._values(sw.baseColor).includes(fc)) return false;
      if (fm && !this._values(sw.material).includes(fm)) return false;
      return true;
    });

    if (!filtered.length) {
      grid.innerHTML = '<p class="swatch-order__empty">Ingen prøver matcher filteret.</p>';
      this._updateNextBtn();
      return;
    }

    // Cards are <div> elements — never <button> — so the info <button> inside
    // is valid HTML and won't be extracted by the parser.
    grid.innerHTML = filtered.map((sw) => {
      const sel = this.selected.has(sw.handle);
      const dim = isMaxed && !sel;
      const number = sel ? order.indexOf(sw.handle) + 1 : null;

      const badge = sw.badge
        ? `<span class="swatch-order__card-badge">${this._esc(sw.badge)}</span>`
        : '';
      const numberBadge = number
        ? `<span class="swatch-order__card-number" aria-hidden="true">${number}</span>`
        : '';
      const img = sw.image
        ? `<img class="swatch-order__card-image" src="${sw.image}" alt="${this._esc(sw.name || '')}" loading="lazy">`
        : '';

      return `<div
        class="swatch-order__card${sel ? ' is-selected' : ''}${dim ? ' is-maxed' : ''}"
        data-swatch-handle="${this._esc(sw.handle)}"
        role="button"
        tabindex="${dim ? '-1' : '0'}"
        aria-pressed="${sel}">
        <div class="swatch-order__card-inner">
          ${img}
          ${badge}
          ${numberBadge}
        </div>
        <div class="swatch-order__card-footer">
          <div class="swatch-order__card-text">
            <span class="swatch-order__card-name">${this._esc(sw.name || '')}</span>
            ${sw.material ? `<span class="swatch-order__card-type">${this._esc(sw.material)}</span>` : ''}
          </div>
          <button type="button" class="swatch-order__info-btn" data-info-handle="${this._esc(sw.handle)}" aria-label="Se detaljer for ${this._esc(sw.name || '')}">
            ${INFO_ICON_SVG}
          </button>
        </div>
      </div>`;
    }).join('');

    this._updateNextBtn();
  }

  // One delegated listener on the stable grid container — survives re-renders.
  _initGridDelegate() {
    const grid = this.querySelector('[data-swatch-grid]');
    if (!grid) return;

    const toggle = (handle) => {
      if (this.selected.has(handle)) {
        this.selected.delete(handle);
      } else if (this.selected.size < this.maxSwatches) {
        this.selected.add(handle);
      }
      this._saveToSession();
      this._renderGrid();
      this._renderSelectedSummary();
    };

    grid.addEventListener('click', (e) => {
      // Info button takes priority
      const infoBtn = e.target.closest('[data-info-handle]');
      if (infoBtn) {
        this._openDetail(infoBtn.dataset.infoHandle);
        return;
      }
      const card = e.target.closest('[data-swatch-handle]');
      if (card) toggle(card.dataset.swatchHandle);
    });

    // Keyboard support for div[role="button"] cards
    grid.addEventListener('keydown', (e) => {
      if (e.key !== 'Enter' && e.key !== ' ') return;
      const card = e.target.closest('[data-swatch-handle]');
      if (!card) return;
      e.preventDefault();
      toggle(card.dataset.swatchHandle);
    });
  }

  _updateNextBtn() {
    const btn = this.querySelector('[data-next-btn]');
    if (btn) btn.disabled = this.selected.size === 0;
  }

  // ─── Detail popup ─────────────────────────────────────────────────────────

  _initDetailPopup() {
    const popup = this.querySelector('[data-detail-popup]');
    if (!popup) return;
    this._popup = popup;

    popup.addEventListener('click', (e) => {
      if (e.target.closest('[data-detail-close]') || e.target === popup) {
        this._closeDetail();
      }
    });

    popup.querySelector('[data-detail-select]')?.addEventListener('click', () => {
      const handle = popup.dataset.currentHandle;
      if (!handle) return;
      if (this.selected.has(handle)) {
        this.selected.delete(handle);
      } else if (this.selected.size < this.maxSwatches) {
        this.selected.add(handle);
      }
      this._saveToSession();
      this._renderGrid();
      this._renderSelectedSummary();
      this._updateDetailSelectBtn(handle);
    });
  }

  // ─── Club terms popup ─────────────────────────────────────────────────────

  _initTermsPopup() {
    const popup = this.querySelector('[data-terms-popup]');
    const openBtn = this.querySelector('[data-terms-open]');
    if (!popup || !openBtn) return;
    this._termsPopup = popup;

    openBtn.addEventListener('click', () => this._openOverlay(popup, () => this._closeTerms()));

    popup.addEventListener('click', (e) => {
      if (e.target.closest('[data-terms-close]') || e.target === popup) {
        this._closeTerms();
      }
    });
  }

  _closeTerms() {
    if (this._termsPopup) this._closeOverlay(this._termsPopup);
  }

  _openDetail(handle) {
    const sw = this.swatchData.find((s) => s.handle === handle);
    if (!sw || !this._popup) return;

    const nameEl = this._popup.querySelector('[data-detail-name]');
    const imgEl = this._popup.querySelector('[data-detail-image]');
    const secondaryImgEl = this._popup.querySelector('[data-detail-image-secondary]');
    const descEl = this._popup.querySelector('[data-detail-description]');
    const specsEl = this._popup.querySelector('[data-detail-specs]');

    if (nameEl) nameEl.textContent = sw.material ? `${sw.name} - ${sw.material}` : sw.name || '';

    if (imgEl) {
      const src = sw.image || '';
      imgEl.src = src;
      imgEl.alt = sw.name || '';
      imgEl.style.display = src ? 'block' : 'none';
    }

    if (secondaryImgEl) {
      const src = sw.secondaryImage || '';
      secondaryImgEl.src = src;
      secondaryImgEl.alt = sw.name || '';
      secondaryImgEl.style.display = src ? 'block' : 'none';
    }

    if (descEl) {
      descEl.innerHTML = sw.description || '';
      descEl.style.display = sw.description ? '' : 'none';
    }

    if (specsEl) {
      const rows = [
        sw.martindale && ['Martindale - slidstyrke', sw.martindale],
        sw.lightFastness && ['Lysægthed - farvebestandighed', sw.lightFastness],
        sw.pilling && ['Pilling - modstand mod fnugdannelse', sw.pilling],
        sw.deliveryNote && ['Levering', sw.deliveryNote],
      ].filter(Boolean);

      specsEl.innerHTML = rows.map(([label, val]) =>
        `<div class="swatch-order__detail-spec"><dt>${this._esc(label)}:</dt><dd>${this._esc(String(val))}</dd></div>`
      ).join('');
      specsEl.style.display = rows.length ? '' : 'none';
    }

    this._popup.dataset.currentHandle = handle;
    this._updateDetailSelectBtn(handle);
    this._openOverlay(this._popup, () => this._closeDetail());
  }

  _updateDetailSelectBtn(handle) {
    const btn = this._popup?.querySelector('[data-detail-select]');
    if (!btn) return;
    const isSel = this.selected.has(handle);
    const isMaxed = this.selected.size >= this.maxSwatches;
    btn.textContent = isSel ? btn.dataset.labelDeselect : btn.dataset.labelSelect;
    btn.disabled = !isSel && isMaxed;
  }

  _closeDetail() {
    if (!this._popup) return;
    delete this._popup.dataset.currentHandle;
    this._closeOverlay(this._popup);
  }

  // ─── Navigation ───────────────────────────────────────────────────────────

  _initNavigation() {
    this.querySelector('[data-next-btn]')?.addEventListener('click', () => this._showStep(2));
    this.querySelector('[data-back-btn]')?.addEventListener('click', () => this._showStep(1));
  }

  _showStep(n) {
    this.querySelector('[data-step="1"]').hidden = n !== 1;
    this.querySelector('[data-step="2"]').hidden = n !== 2;
    if (n === 2) {
      this._renderSelectedSummary();
      this._populateSwatchField();
    }
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  // Delegated on the custom element itself, so it covers every
  // [data-selected-summary] instance even after _renderSelectedSummary
  // replaces their contents.
  _initSummaryRemove() {
    this.addEventListener('click', (e) => {
      const removeBtn = e.target.closest('[data-summary-remove]');
      if (!removeBtn) return;
      const handle = removeBtn.closest('[data-summary-handle]')?.dataset.summaryHandle;
      if (!handle) return;

      this.selected.delete(handle);
      this._saveToSession();
      this._renderGrid();
      this._renderSelectedSummary();
    });
  }

  // ─── Step 2 ───────────────────────────────────────────────────────────────

  _renderSelectedSummary() {
    const els = this.querySelectorAll('[data-selected-summary]');
    if (!els.length) return;

    const order = [...this.selected];
    const slots = Array.from({ length: this.maxSwatches }, (_, i) => {
      const handle = order[i];
      return handle ? this.swatchData.find((sw) => sw.handle === handle) : null;
    });

    const html = slots.map((sw, i) => {
      if (!sw) return '<div class="swatch-order__summary-slot swatch-order__summary-slot--empty"></div>';

      return `<div class="swatch-order__summary-slot" data-summary-handle="${this._esc(sw.handle)}">
        ${sw.image
          ? `<img class="swatch-order__summary-image" src="${sw.image}" alt="${this._esc(sw.name || '')}" loading="lazy">`
          : ''}
        <span class="swatch-order__card-number" aria-hidden="true">${i + 1}</span>
        <button type="button" class="swatch-order__summary-remove" data-summary-remove aria-label="Fjern ${this._esc(sw.name || '')}">
          <svg width="24" height="24" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2" fill="none" stroke-linecap="round"><path d="M5 19 19 5M5 5l14 14"/></svg>
        </button>
      </div>`;
    }).join('');

    els.forEach((el) => { el.innerHTML = html; });
  }

  _populateSwatchField() {
    const field = this.querySelector('[data-swatches-field]');
    if (!field) return;
    field.value = this.swatchData
      .filter((sw) => this.selected.has(sw.handle))
      .map((sw) => sw.name || sw.handle)
      .join(', ');
  }

  // Home Lovers opt-in only makes sense once the customer has consented to
  // being contacted, so the banner tracks the consent checkbox directly.
  _initConsentToggle() {
    const consent = this.querySelector('[data-consent-checkbox]');
    const banner = this.querySelector('[data-home-lovers-banner]');
    const homeLoversConsent = this.querySelector('[data-home-lovers-consent]');
    if (!consent || !banner) return;

    // Home Lovers membership needs a phone number, so the otherwise optional
    // phone field becomes required (with its asterisk) while the opt-in is ticked.
    const phoneInput = this.querySelector('[data-phone-input]');
    const phoneMark = this.querySelector('[data-phone-required-mark]');
    const syncPhoneRequired = () => {
      const required = !!homeLoversConsent?.checked;
      if (phoneInput) {
        phoneInput.required = required;
        if (required) phoneInput.setAttribute('aria-required', 'true');
        else phoneInput.removeAttribute('aria-required');
      }
      if (phoneMark) phoneMark.hidden = !required;
    };
    homeLoversConsent?.addEventListener('change', syncPhoneRequired);
    syncPhoneRequired();

    consent.addEventListener('change', () => {
      banner.hidden = !consent.checked;
      if (!consent.checked && homeLoversConsent) homeLoversConsent.checked = false;
      syncPhoneRequired();
    });
  }

  _initFormSubmit() {
    const form = this.closest('form');
    if (!form) return;

    form.addEventListener('submit', (e) => {
      e.preventDefault();
      this._populateSwatchField();
      this._submitForm(form);
    });
  }

  async _submitForm(form) {
    const endpoint = form.dataset.endpoint;
    const submitBtn = form.querySelector('[data-submit-btn]');
    const errorEl = form.querySelector('[data-submit-error]');
    const swatchesField = form.querySelector('[data-swatches-field]');
    const formData = new FormData(form);

    const payload = {
      formId: 'ilva-swatch-order',
      name: formData.get('name') || '',
      email: formData.get('email') || '',
      phone: formData.get('phone') || '',
      address: formData.get('address') || '',
      postcode: formData.get('postcode') || '',
      city: formData.get('city') || '',
      consent: formData.has('consent'),
      sofaInterest: formData.get('sofa_interest') || '',
      homeLovers: formData.has('home_lovers'),
      swatchesLabel: swatchesField?.dataset.fieldLabel || '',
      swatchesText: swatchesField?.value || '',
      swatches: this.swatchData
        .filter((sw) => this.selected.has(sw.handle))
        .map((sw) => ({ handle: sw.handle, name: sw.name })),
    };

    errorEl?.setAttribute('hidden', '');
    if (submitBtn) {
      submitBtn.disabled = true;
      submitBtn.textContent = submitBtn.dataset.labelLoading || submitBtn.textContent;
    }

    try {
      const res = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error(`Request failed: ${res.status}`);

      this._clearSession();
      this._showSuccess();
    } catch (_) {
      errorEl?.removeAttribute('hidden');
      if (submitBtn) {
        submitBtn.disabled = false;
        submitBtn.textContent = submitBtn.dataset.labelDefault || submitBtn.textContent;
      }
    }
  }

  _showSuccess() {
    const page = this.closest('.swatch-order-page');
    page?.querySelector('[data-success]')?.removeAttribute('hidden');
    this.closest('form')?.setAttribute('hidden', '');
  }

  // ─── Utils ────────────────────────────────────────────────────────────────

  _esc(str) {
    return String(str)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;')
      .replace(/>/g, '&gt;').replace(/"/g, '&quot;').replace(/'/g, '&#39;');
  }
}

customElements.define('swatch-order-form', SwatchOrderForm);
